/**
 * Render service — wraps @remotion/renderer to render the project to MP4.
 *
 * Uses bundle() + selectComposition() + renderMedia() flow.
 * The render runs in a child process so it doesn't block the MCP stdio channel.
 */

import { promises as fs } from 'node:fs';
import path from 'node:path';
import { spawn } from 'node:child_process';
import type { ProjectManager } from './project.js';

export interface RenderResult {
  outputPath: string;
  durationSeconds: number;
}

export interface RenderProgress {
  progress: number;
  stage: 'bundling' | 'rendering' | 'encoding' | 'done' | 'error';
  message?: string;
}

/**
 * Renders the project to MP4 by spawning a standalone render script.
 * We use a child process instead of calling @remotion/renderer directly
 * because the bundler + Chromium can be heavy and we don't want to
 * block the MCP stdio transport.
 */
export async function renderVideo(
  project: ProjectManager,
  outputPath?: string,
  onProgress?: (progress: RenderProgress) => void,
): Promise<RenderResult> {
  const out =
    outputPath ?? path.join(project.dir, 'out', `video-${Date.now()}.mp4`);
  await fs.mkdir(path.dirname(out), { recursive: true });

  return new Promise((resolve, reject) => {
    const script = `
import { bundle } from '@remotion/bundler';
import { renderMedia, selectComposition } from '@remotion/renderer';
import path from 'node:path';

const projectDir = ${JSON.stringify(project.dir)};
const outputPath = ${JSON.stringify(out)};

const entryPoint = path.join(projectDir, 'src', 'index.tsx');

const start = Date.now();

(async () => {
  try {
    process.stderr.write(JSON.stringify({ stage: 'bundling', progress: 0 }) + '\\n');
    const bundleLocation = await bundle({ entryPoint });
    process.stderr.write(JSON.stringify({ stage: 'rendering', progress: 0 }) + '\\n');

    const composition = await selectComposition({
      serveUrl: bundleLocation,
      id: 'SibrikComposition',
      inputProps: {},
    });

    await renderMedia({
      composition,
      serveUrl: bundleLocation,
      codec: 'h264',
      outputLocation: outputPath,
      inputProps: {},
      onProgress: ({ progress }) => {
        process.stderr.write(JSON.stringify({ stage: 'rendering', progress }) + '\\n');
      },
    });

    process.stderr.write(JSON.stringify({ stage: 'done', progress: 1 }) + '\\n');
    process.stdout.write(JSON.stringify({ outputPath, durationSeconds: (Date.now() - start) / 1000 }));
  } catch (err) {
    process.stderr.write(JSON.stringify({ stage: 'error', message: err.message }) + '\\n');
    process.exit(1);
  }
})();
`;

    const child = spawn('node', ['--input-type=module', '-e', script], {
      cwd: project.dir,
      stdio: ['pipe', 'pipe', 'pipe'],
      env: { ...process.env },
    });

    let stdout = '';
    let lastProgress = 0;

    child.stderr.on('data', (data: Buffer) => {
      const lines = data.toString().split('\n').filter(Boolean);
      for (const line of lines) {
        try {
          const msg = JSON.parse(line);
          if (msg.stage === 'bundling') {
            onProgress?.({ progress: 0, stage: 'bundling' });
          } else if (msg.stage === 'rendering') {
            lastProgress = msg.progress;
            onProgress?.({ progress: msg.progress, stage: 'rendering' });
          } else if (msg.stage === 'done') {
            onProgress?.({ progress: 1, stage: 'done' });
          } else if (msg.stage === 'error') {
            onProgress?.({ progress: 0, stage: 'error', message: msg.message });
          }
        } catch {
          // Not JSON — ignore (Remotion debug output)
        }
      }
    });

    child.stdout.on('data', (data: Buffer) => {
      stdout += data.toString();
    });

    child.on('close', (code: number | null) => {
      if (code !== 0) {
        reject(new Error(`Render process exited with code ${code}`));
        return;
      }
      try {
        const result = JSON.parse(stdout.trim());
        resolve({
          outputPath: result.outputPath,
          durationSeconds: result.durationSeconds,
        });
      } catch {
        // Fallback: return the output path even if parsing fails
        resolve({ outputPath: out, durationSeconds: 0 });
      }
    });

    child.on('error', (err: Error) => {
      reject(new Error(`Failed to start render process: ${err.message}`));
    });
  });
}

/**
 * Launches Remotion Studio in the project directory.
 * Returns the localhost URL where the user can see the preview.
 */
export function startStudio(
  project: ProjectManager,
  port = 3000,
): { process: ReturnType<typeof spawn>; url: string } {
  const child = spawn(
    'npx',
    ['remotion', 'studio', 'src/index.tsx', '--port', String(port), '--no-open'],
    {
      cwd: project.dir,
      stdio: ['ignore', 'pipe', 'pipe'],
      env: { ...process.env },
      shell: process.platform === 'win32',
      detached: true,
    },
  );

  const url = `http://localhost:${port}`;

  // Log studio output for debugging
  child.stdout?.on('data', (data: Buffer) => {
    process.stderr.write(`[studio] ${data}`);
  });
  child.stderr?.on('data', (data: Buffer) => {
    process.stderr.write(`[studio] ${data}`);
  });

  return { process: child, url };
}
